import { usePathname } from 'next/navigation';
import { useLayoutEffect, useRef, useState, type FC } from 'react';
import { useSession } from 'next-auth/react';
import { Box, Flex, Text } from '@eqi-investimentos/react';
import { fallbackMenu, type MenuType } from '@/modules/Customer/interface/Menu';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useGetMenu } from '@/modules/Customer/http/client/useGetMenu';
import { calculateVisibleItems, handleMenuItemClick } from './utils';
import { MenuItem } from './Item';
import { MenuSkeleton } from './Skeleton';
import { Submenu } from './Submenu';

export const Menu: FC<MenuProps> = ({ isVisible = true }) => {
  const pathname = usePathname();
  const { data: session } = useSession();

  const { data: menu = fallbackMenu, isPending: isMenuLoading } = useGetMenu();
  const { mutate: sendClickEvent } = useSendClickEvent();

  const containerRef = useRef<HTMLDivElement>(null);
  const moreItemRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [formattedMenu, setFormattedMenu] = useState<MenuType[]>([]);
  const [urls, setUrls] = useState<string[]>([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || isMenuLoading) return;

    const calculate = () => {
      const { items, urls } = calculateVisibleItems(
        menu,
        container.offsetWidth,
        moreItemRef.current?.offsetWidth || 124,
        itemRefs.current
      );

      setFormattedMenu(items);
      setUrls(urls);
    };

    calculate();

    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [menu, isMenuLoading]);

  if (isMenuLoading) return <MenuSkeleton />;

  return (
    <>
      <Flex
        position="absolute"
        visibility="hidden"
        pointerEvents="none"
        aria-hidden
      >
        <Box ref={moreItemRef} w="7.75rem" />
        {menu.map(({ name }) => (
          <Text
            key={`measure-${name}`}
            ref={(el) => {
              if (el) {
                itemRefs.current.set(name, el);
              }
            }}
            minW="7rem"
            maxW="max-content"
            whiteSpace="nowrap"
          >
            {name}
          </Text>
        ))}
      </Flex>

      <Flex
        ref={containerRef}
        alignItems="center"
        justifyContent="center"
        w="full"
        gap="2xs"
        py="2xl"
        borderBottomWidth="0.0625rem"
        borderBottomColor="transparency.light"
        transition="all 500ms ease-in-out"
      >
        {formattedMenu.map(({ iconName, name, target, url, submenu }) =>
          submenu ? (
            <Submenu
              key={`menu-item-${name}`}
              buttonName={name}
              icon={iconName}
              items={submenu}
              shouldClose={!isVisible}
            />
          ) : (
            <MenuItem
              key={`menu-item-${name}`}
              iconName={iconName}
              isActive={
                urls.includes(pathname)
                  ? pathname === url
                  : pathname.includes(url || '')
              }
              name={name}
              target={target}
              url={url}
              onClick={() =>
                handleMenuItemClick(
                  url,
                  !!session?.access_token,
                  sendClickEvent
                )
              }
            />
          )
        )}
      </Flex>
    </>
  );
};

type MenuProps = {
  isVisible?: boolean;
};
